import {
  checkRadius,
  DRIVE_ZONE_PRECOMPUTED_DRIVING_MODE,
  DRIVE_ZONES_PRECOMPUTED_DRIVING_DISCLAIMER,
  RADIUS_DISTANCE_UNITS,
  RADIUS_STRAIGHT_LINE_DISCLAIMER,
  RADIUS_STRAIGHT_LINE_MODE,
  type DriveZoneAttribution,
  type DriveZoneOrigin,
  type DriveZoneRule,
  type RadiusCheckResult,
  type RadiusOrigin,
  type RadiusRule,
  type StreetKitSelectedAddress
} from "@streetkit/shared";

import type { StreetKitFetch } from "./fetch-index.js";

export type StreetKitRadiusStatus =
  | "ok"
  | "no_coordinates"
  | "no_origin"
  | "outside_rules"
  | "config_error";

export type StreetKitRadiusOriginConfig = {
  id: string;
  label: string;
  lat: number;
  lon: number;
};

export type StreetKitRadiusRuleConfig = Omit<RadiusRule, "radiusKm"> & {
  radiusKm?: number;
  maxDistanceKm?: number;
};

export type StreetKitRadiusRulesConfig = {
  mode?: "straight_line";
  origin?: StreetKitRadiusOriginConfig;
  origins?: StreetKitRadiusOriginConfig[];
  rules?: StreetKitRadiusRuleConfig[];
};

export type StreetKitRadiusContext = RadiusCheckResult & {
  status: StreetKitRadiusStatus;
  origin: StreetKitRadiusOriginConfig | null;
  distance: {
    metres: number;
    km: number;
    roundedKm: number;
  } | null;
  matchedRules: RadiusRule[];
  wording: typeof RADIUS_STRAIGHT_LINE_DISCLAIMER;
};

export type StreetKitDeliveryZoneStatus =
  | "eligible"
  | "quote_required"
  | "outside_standard_area"
  | "unrouteable"
  | "no_coordinates"
  | "not_precomputed"
  | "manifest_unavailable"
  | "shard_unavailable"
  | "version_mismatch"
  | "config_error";

export type StreetKitDeliveryZonesConfig = {
  enabled?: boolean;
  baseUrl?: string;
  metaUrl?: string;
  mode?: "precomputed_driving";
};

export type StreetKitDriveZonesConfig = StreetKitDeliveryZonesConfig & {
  attributionMode?: "return";
};

export type StreetKitDeliveryZoneRule = {
  id: string;
  label: string;
  status?: StreetKitDeliveryZoneStatus;
  maxDistanceKm?: number;
  fee?: number | null;
  message?: string;
  displayOrder?: number;
};

export type StreetKitDeliveryZoneResult = {
  available: boolean;
  mode: "precomputed_driving";
  status: StreetKitDeliveryZoneStatus;
  addressId: string;
  zoneIndexVersion: string | null;
  addressIndexVersion: string | null;
  distanceKm: number | null;
  distanceMetres: number | null;
  nearestOrigin: DriveZoneOrigin | null;
  origin: DriveZoneOrigin | null;
  zone: StreetKitDeliveryZoneRule | null;
  rule: StreetKitDeliveryZoneRule | null;
  matchedRule: StreetKitDeliveryZoneRule | null;
  message: string;
  disclaimer: typeof DRIVE_ZONES_PRECOMPUTED_DRIVING_DISCLAIMER | string;
  attribution: DriveZoneAttribution | null;
};

export type StreetKitSelectionContext = {
  radius?: StreetKitRadiusContext;
  delivery?: StreetKitDeliveryZoneResult;
  driveZones?: StreetKitDeliveryZoneResult;
};

export type StreetKitSelectionContextConfig = {
  radius?: StreetKitRadiusRulesConfig;
  radiusRules?: StreetKitRadiusRulesConfig;
  deliveryZones?: StreetKitDeliveryZonesConfig;
  driveZones?: StreetKitDriveZonesConfig;
  fetcher?: StreetKitFetch;
};

type DeliveryZonesMetaPayload = {
  schemaVersion?: string;
  schema_version?: string;
  manifestPath?: string;
  manifest_url?: string;
  manifestUrl?: string;
  zoneIndexVersion?: string;
  currentVersion?: string;
  version?: string;
  addressIndexVersion?: string;
  mode?: string;
  shardKeyStrategy?: string;
  shardKeyLength?: number;
  shardPathTemplate?: string;
  attribution?: unknown;
  disclaimer?: string;
};

type DeliveryZonesManifestPayload = {
  schemaVersion?: string;
  schema_version?: string;
  version?: string;
  zoneIndexVersion?: string;
  addressIndexVersion?: string;
  mode?: string;
  shardKeyStrategy?: string;
  shardKeyLength?: number;
  shardPathTemplate?: string;
  origins?: unknown;
  rules?: unknown;
  attribution?: unknown;
  disclaimer?: string;
};

type DeliveryZonesShardPayload = {
  schemaVersion?: string;
  schema_version?: string;
  version?: string;
  zoneIndexVersion?: string;
  addressIndexVersion?: string;
  shardKey?: string;
  results?: unknown;
};

type CompactDeliveryZoneRecord = {
  s?: unknown;
  o?: unknown;
  r?: unknown;
  m?: unknown;
  km?: unknown;
  status?: unknown;
  originId?: unknown;
  matchedRuleId?: unknown;
  drivingDistanceMetres?: unknown;
  drivingDistanceKm?: unknown;
};

type DeliveryZoneLookupConfig = {
  enabled: boolean;
  metaUrl: string | null;
  mode: "precomputed_driving" | null;
};

export class StreetKitDeliveryZoneLookupClient {
  private readonly fetcher: StreetKitFetch;
  private readonly config: DeliveryZoneLookupConfig;
  private metaPromise: Promise<DeliveryZonesMetaPayload | null> | null = null;
  private readonly manifestCache = new Map<string, Promise<DeliveryZonesManifestPayload | null>>();
  private readonly shardCache = new Map<string, Promise<DeliveryZonesShardPayload | null>>();

  constructor(config: StreetKitDeliveryZonesConfig | StreetKitDriveZonesConfig, fetcher?: StreetKitFetch) {
    this.fetcher = fetcher ?? globalThis.fetch.bind(globalThis);
    this.config = normaliseDeliveryZoneLookupConfig(config);
  }

  async lookup(address: StreetKitSelectedAddress): Promise<StreetKitDeliveryZoneResult> {
    if (this.config.mode !== DRIVE_ZONE_PRECOMPUTED_DRIVING_MODE || this.config.metaUrl === null) {
      return deliveryUnavailable({
        address,
        status: "config_error",
        message:
          "StreetKit deliveryZones requires precomputed_driving mode and a delivery meta URL."
      });
    }

    const meta = await this.fetchMeta();

    if (meta === null) {
      return deliveryUnavailable({
        address,
        status: "manifest_unavailable",
        message: "StreetKit Drive Zones metadata is unavailable."
      });
    }

    const manifestPath = stringFrom(meta.manifestPath, meta.manifest_url, meta.manifestUrl);

    if (manifestPath === null) {
      return deliveryUnavailable({
        address,
        status: "config_error",
        zoneIndexVersion: stringFrom(meta.zoneIndexVersion, meta.currentVersion, meta.version),
        addressIndexVersion: stringFrom(meta.addressIndexVersion),
        attribution: attributionFrom(meta.attribution),
        disclaimer: disclaimerFrom(meta.disclaimer),
        message: "StreetKit Drive Zones metadata does not include a manifest path."
      });
    }

    const manifestUrl = resolveDeliveryZoneUrl(this.config.metaUrl, manifestPath);
    const manifest = await this.fetchManifest(manifestUrl);

    if (manifest === null) {
      return deliveryUnavailable({
        address,
        status: "manifest_unavailable",
        zoneIndexVersion: stringFrom(meta.zoneIndexVersion, meta.currentVersion, meta.version),
        addressIndexVersion: stringFrom(meta.addressIndexVersion),
        attribution: attributionFrom(meta.attribution),
        disclaimer: disclaimerFrom(meta.disclaimer),
        message: "StreetKit Drive Zones manifest is unavailable."
      });
    }

    const zoneIndexVersion = stringFrom(manifest.zoneIndexVersion, manifest.version);
    const addressIndexVersion = stringFrom(manifest.addressIndexVersion);
    const attribution = attributionFrom(manifest.attribution) ?? attributionFrom(meta.attribution);
    const disclaimer = disclaimerFrom(manifest.disclaimer, meta.disclaimer);

    if (
      addressIndexVersion !== null &&
      address.source.version.trim().length > 0 &&
      addressIndexVersion !== address.source.version
    ) {
      return deliveryUnavailable({
        address,
        status: "version_mismatch",
        zoneIndexVersion,
        addressIndexVersion,
        attribution,
        disclaimer,
        message: "StreetKit Drive Zones output does not match the selected address index version."
      });
    }

    if (!isSupportedShardStrategy(stringFrom(manifest.shardKeyStrategy, meta.shardKeyStrategy))) {
      return deliveryUnavailable({
        address,
        status: "config_error",
        zoneIndexVersion,
        addressIndexVersion,
        attribution,
        disclaimer,
        message: "StreetKit Drive Zones shard key strategy is unsupported by this widget."
      });
    }

    const shardKeyLength = positiveIntegerFrom(manifest.shardKeyLength, meta.shardKeyLength);
    const shardPathTemplate = stringFrom(manifest.shardPathTemplate, meta.shardPathTemplate);

    if (shardKeyLength === null || shardPathTemplate === null) {
      return deliveryUnavailable({
        address,
        status: "config_error",
        zoneIndexVersion,
        addressIndexVersion,
        attribution,
        disclaimer,
        message: "StreetKit Drive Zones manifest does not include shard path information."
      });
    }

    let shardKey: string;

    try {
      shardKey = (await sha256Hex(address.id)).slice(0, shardKeyLength);
    } catch {
      return deliveryUnavailable({
        address,
        status: "config_error",
        zoneIndexVersion,
        addressIndexVersion,
        attribution,
        disclaimer,
        message: "StreetKit Drive Zones lookup requires browser SHA-256 support."
      });
    }

    const shardPath = shardPathTemplate.replaceAll("{key}", shardKey);
    const shardUrl = resolveDeliveryZoneUrl(manifestUrl, shardPath);
    const shard = await this.fetchShard(shardUrl);

    if (shard === null || !isRecord(shard.results)) {
      return deliveryUnavailable({
        address,
        status: "shard_unavailable",
        zoneIndexVersion,
        addressIndexVersion,
        attribution,
        disclaimer,
        message: "StreetKit Drive Zones result shard is unavailable."
      });
    }

    const rawRecord = shard.results[address.id];

    if (!isRecord(rawRecord)) {
      return deliveryUnavailable({
        address,
        status: "not_precomputed",
        zoneIndexVersion,
        addressIndexVersion,
        attribution,
        disclaimer,
        message: "Selected address is not present in the precomputed Drive Zones shard."
      });
    }

    return deliveryResultFromRecord({
      address,
      record: rawRecord,
      manifest,
      zoneIndexVersion,
      addressIndexVersion,
      attribution,
      disclaimer
    });
  }

  private fetchMeta(): Promise<DeliveryZonesMetaPayload | null> {
    if (this.metaPromise === null) {
      this.metaPromise = this.fetchJson<DeliveryZonesMetaPayload>(this.config.metaUrl ?? "");
    }

    return this.metaPromise;
  }

  private fetchManifest(url: string): Promise<DeliveryZonesManifestPayload | null> {
    const cached = this.manifestCache.get(url);

    if (cached !== undefined) {
      return cached;
    }

    const request = this.fetchJson<DeliveryZonesManifestPayload>(url);
    this.manifestCache.set(url, request);
    return request;
  }

  private fetchShard(url: string): Promise<DeliveryZonesShardPayload | null> {
    const cached = this.shardCache.get(url);

    if (cached !== undefined) {
      return cached;
    }

    const request = this.fetchJson<DeliveryZonesShardPayload>(url);
    this.shardCache.set(url, request);
    return request;
  }

  private async fetchJson<T>(url: string): Promise<T | null> {
    if (url.trim().length === 0) {
      return null;
    }

    try {
      const response = await this.fetcher(url, { credentials: "omit" });

      if (!response.ok) {
        return null;
      }

      return (await response.json()) as T;
    } catch {
      return null;
    }
  }
}

export function createStreetKitDeliveryZoneLookup(
  config: StreetKitSelectionContextConfig
): StreetKitDeliveryZoneLookupClient | null {
  const deliveryConfig = config.deliveryZones ?? config.driveZones;

  if (deliveryConfig === undefined || deliveryConfig.enabled === false) {
    return null;
  }

  return new StreetKitDeliveryZoneLookupClient(deliveryConfig, config.fetcher);
}

export function evaluateStreetKitRadius(
  address: StreetKitSelectedAddress,
  config: StreetKitRadiusRulesConfig | undefined
): StreetKitRadiusContext | undefined {
  if (config === undefined) {
    return undefined;
  }

  if (config.mode !== undefined && config.mode !== RADIUS_STRAIGHT_LINE_MODE) {
    return radiusUnavailable("config_error", "StreetKit Radius only supports straight_line mode.");
  }

  if (!hasSelectedCoordinates(address)) {
    return radiusUnavailable(
      "no_coordinates",
      "Selected address coordinates are missing or invalid."
    );
  }

  const origins = normaliseRadiusOrigins(config);

  if (origins.length === 0) {
    return radiusUnavailable("no_origin", "No radius origins are configured.");
  }

  const rules = normaliseRadiusRules(config);

  if (rules.length === 0) {
    return radiusUnavailable("config_error", "No radius rules are configured.");
  }

  const result = checkRadius({
    destination: {
      lat: address.location.lat,
      lon: address.location.lon
    },
    origins,
    rules
  });
  const status = statusForRadiusResult(result, rules);

  return toRadiusContext(status, result);
}

export function hasStreetKitSelectionContext(context: StreetKitSelectionContext): boolean {
  return (
    context.radius !== undefined ||
    context.delivery !== undefined ||
    context.driveZones !== undefined
  );
}

function statusForRadiusResult(
  result: RadiusCheckResult,
  rules: readonly RadiusRule[]
): StreetKitRadiusStatus {
  if (result.distanceKm === null) {
    return result.nearestOrigin === null ? "no_origin" : "config_error";
  }

  if (rules.length === 0) {
    return "config_error";
  }

  return result.eligible ? "ok" : "outside_rules";
}

function toRadiusContext(
  status: StreetKitRadiusStatus,
  result: RadiusCheckResult
): StreetKitRadiusContext {
  const distance =
    result.distanceKm === null
      ? null
      : {
          metres: result.distanceKm * 1000,
          km: result.distanceKm,
          roundedKm: Number(result.distanceKm.toFixed(1))
        };
  const origin =
    result.nearestOrigin === null
      ? null
      : {
          id: result.nearestOrigin.origin.id,
          label: result.nearestOrigin.origin.label,
          lat: result.nearestOrigin.origin.coordinates.lat,
          lon: result.nearestOrigin.origin.coordinates.lon
        };

  return {
    ...result,
    status,
    origin,
    distance,
    matchedRules: result.eligibleRules,
    wording: RADIUS_STRAIGHT_LINE_DISCLAIMER
  };
}

function radiusUnavailable(
  status: StreetKitRadiusStatus,
  message: string
): StreetKitRadiusContext {
  return toRadiusContext(status, {
    mode: RADIUS_STRAIGHT_LINE_MODE,
    units: RADIUS_DISTANCE_UNITS,
    distanceKm: null,
    nearestOrigin: null,
    eligibleRules: [],
    eligible: false,
    message,
    disclaimer: RADIUS_STRAIGHT_LINE_DISCLAIMER
  });
}

function normaliseRadiusOrigins(config: StreetKitRadiusRulesConfig): RadiusOrigin[] {
  const originConfigs =
    config.origins !== undefined
      ? config.origins
      : config.origin === undefined
        ? []
        : [config.origin];

  return originConfigs.map((origin) => ({
    id: origin.id,
    label: origin.label,
    coordinates: {
      lat: origin.lat,
      lon: origin.lon
    }
  }));
}

function normaliseRadiusRules(config: StreetKitRadiusRulesConfig): RadiusRule[] {
  return (config.rules ?? [])
    .map((rule) => {
      const radiusKm = numberFrom(rule.radiusKm, rule.maxDistanceKm);

      if (radiusKm === null) {
        return null;
      }

      const normalised: RadiusRule = {
        id: rule.id,
        label: rule.label,
        radiusKm
      };

      if ("fee" in rule) {
        normalised.fee = rule.fee ?? null;
      }

      if (rule.message !== undefined) {
        normalised.message = rule.message;
      }

      if (rule.priority !== undefined) {
        normalised.priority = rule.priority;
      }

      if (rule.displayOrder !== undefined) {
        normalised.displayOrder = rule.displayOrder;
      }

      return normalised;
    })
    .filter((rule): rule is RadiusRule => rule !== null);
}

function hasSelectedCoordinates(
  address: StreetKitSelectedAddress
): address is StreetKitSelectedAddress & {
  location: {
    lat: number;
    lon: number;
  };
} {
  return (
    typeof address.location.lat === "number" &&
    typeof address.location.lon === "number" &&
    Number.isFinite(address.location.lat) &&
    Number.isFinite(address.location.lon)
  );
}

function normaliseDeliveryZoneLookupConfig(
  config: StreetKitDeliveryZonesConfig | StreetKitDriveZonesConfig
): DeliveryZoneLookupConfig {
  const mode = config.mode ?? DRIVE_ZONE_PRECOMPUTED_DRIVING_MODE;

  return {
    enabled: config.enabled !== false,
    metaUrl: metaUrlFromDeliveryConfig(config),
    mode: mode === DRIVE_ZONE_PRECOMPUTED_DRIVING_MODE ? mode : null
  };
}

function metaUrlFromDeliveryConfig(
  config: StreetKitDeliveryZonesConfig | StreetKitDriveZonesConfig
): string | null {
  const configuredMetaUrl = trimmedString(config.metaUrl);

  if (configuredMetaUrl !== null) {
    return configuredMetaUrl;
  }

  const baseUrl = trimmedString(config.baseUrl);

  if (baseUrl === null) {
    return null;
  }

  return /\.json(?:[?#]|$)/u.test(baseUrl) ? baseUrl : `${baseUrl.replace(/\/+$/u, "")}/meta.json`;
}

function deliveryResultFromRecord(options: {
  address: StreetKitSelectedAddress;
  record: CompactDeliveryZoneRecord;
  manifest: DeliveryZonesManifestPayload;
  zoneIndexVersion: string | null;
  addressIndexVersion: string | null;
  attribution: DriveZoneAttribution | null;
  disclaimer: string;
}): StreetKitDeliveryZoneResult {
  const status = deliveryStatusFrom(options.record.s, options.record.status) ?? "not_precomputed";
  const originId = stringFrom(options.record.o, options.record.originId);
  const matchedRuleId = stringFrom(options.record.r, options.record.matchedRuleId);
  const origins = originListFrom(options.manifest.origins);
  const rules = ruleListFrom(options.manifest.rules);
  const origin = originId === null ? null : origins.find((candidate) => candidate.id === originId) ?? null;
  const rule = matchedRuleId === null ? null : rules.find((candidate) => candidate.id === matchedRuleId) ?? null;
  const distanceMetres = numberFrom(options.record.m, options.record.drivingDistanceMetres);
  const distanceKm = numberFrom(options.record.km, options.record.drivingDistanceKm);
  const message = rule?.message ?? messageForDeliveryStatus(status);

  return {
    available: status === "eligible" || status === "quote_required",
    mode: DRIVE_ZONE_PRECOMPUTED_DRIVING_MODE,
    status,
    addressId: options.address.id,
    zoneIndexVersion: options.zoneIndexVersion,
    addressIndexVersion: options.addressIndexVersion,
    distanceKm,
    distanceMetres,
    nearestOrigin: origin,
    origin,
    zone: rule,
    rule,
    matchedRule: rule,
    message,
    disclaimer: options.disclaimer,
    attribution: options.attribution
  };
}

function deliveryUnavailable(options: {
  address: StreetKitSelectedAddress;
  status: Extract<
    StreetKitDeliveryZoneStatus,
    | "manifest_unavailable"
    | "shard_unavailable"
    | "version_mismatch"
    | "config_error"
    | "not_precomputed"
  >;
  message: string;
  zoneIndexVersion?: string | null;
  addressIndexVersion?: string | null;
  attribution?: DriveZoneAttribution | null;
  disclaimer?: string;
}): StreetKitDeliveryZoneResult {
  return {
    available: false,
    mode: DRIVE_ZONE_PRECOMPUTED_DRIVING_MODE,
    status: options.status,
    addressId: options.address.id,
    zoneIndexVersion: options.zoneIndexVersion ?? null,
    addressIndexVersion: options.addressIndexVersion ?? null,
    distanceKm: null,
    distanceMetres: null,
    nearestOrigin: null,
    origin: null,
    zone: null,
    rule: null,
    matchedRule: null,
    message: options.message,
    disclaimer: options.disclaimer ?? DRIVE_ZONES_PRECOMPUTED_DRIVING_DISCLAIMER,
    attribution: options.attribution ?? null
  };
}

function originListFrom(value: unknown): DriveZoneOrigin[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isDriveZoneOrigin);
}

function ruleListFrom(value: unknown): StreetKitDeliveryZoneRule[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord).flatMap((rule) => {
    const id = stringFrom(rule.id);
    const label = stringFrom(rule.label);

    if (id === null || label === null) {
      return [];
    }

    const normalised: StreetKitDeliveryZoneRule = {
      id,
      label
    };
    const status = deliveryStatusFrom(rule.status);
    const maxDistanceKm = numberFrom(rule.maxDistanceKm, rule.maxDrivingDistanceKm);
    const fee = numberFrom(rule.fee);
    const message = stringFrom(rule.message);
    const displayOrder = numberFrom(rule.displayOrder);

    if (status !== null) {
      normalised.status = status;
    }

    if (maxDistanceKm !== null) {
      normalised.maxDistanceKm = maxDistanceKm;
    }

    if ("fee" in rule) {
      normalised.fee = fee;
    }

    if (message !== null) {
      normalised.message = message;
    }

    if (displayOrder !== null) {
      normalised.displayOrder = displayOrder;
    }

    return [normalised];
  });
}

function isDriveZoneOrigin(value: unknown): value is DriveZoneOrigin {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === "string" &&
    typeof value.label === "string" &&
    typeof value.lat === "number" &&
    Number.isFinite(value.lat) &&
    typeof value.lon === "number" &&
    Number.isFinite(value.lon)
  );
}

function deliveryStatusFrom(...values: unknown[]): StreetKitDeliveryZoneStatus | null {
  for (const value of values) {
    if (
      value === "eligible" ||
      value === "quote_required" ||
      value === "outside_standard_area" ||
      value === "unrouteable" ||
      value === "no_coordinates" ||
      value === "not_precomputed" ||
      value === "manifest_unavailable" ||
      value === "shard_unavailable" ||
      value === "version_mismatch" ||
      value === "config_error"
    ) {
      return value;
    }
  }

  return null;
}

function messageForDeliveryStatus(status: StreetKitDeliveryZoneStatus): string {
  switch (status) {
    case "eligible":
      return "Selected address is within the precomputed Drive Zones service area.";
    case "quote_required":
      return "Selected address requires a delivery quote in the precomputed Drive Zones result.";
    case "outside_standard_area":
      return "Selected address is outside the standard precomputed Drive Zones service area.";
    case "unrouteable":
      return "Selected address was not routeable when the Drive Zones index was built.";
    case "no_coordinates":
      return "Selected address did not have coordinates when the Drive Zones index was built.";
    case "not_precomputed":
      return "Selected address is not present in the precomputed Drive Zones shard.";
    case "manifest_unavailable":
      return "StreetKit Drive Zones manifest is unavailable.";
    case "shard_unavailable":
      return "StreetKit Drive Zones result shard is unavailable.";
    case "version_mismatch":
      return "StreetKit Drive Zones output does not match the selected address index version.";
    case "config_error":
      return "StreetKit Drive Zones configuration is invalid.";
  }
}

function attributionFrom(value: unknown): DriveZoneAttribution | null {
  if (!isRecord(value) || typeof value.text !== "string" || !Array.isArray(value.urls)) {
    return null;
  }

  const urls = value.urls.filter((url): url is string => typeof url === "string");

  if (value.text.trim().length === 0 || urls.length === 0) {
    return null;
  }

  return {
    text: value.text,
    urls
  };
}

function disclaimerFrom(...values: Array<string | undefined>): string {
  return values.find((value): value is string => typeof value === "string" && value.trim().length > 0) ??
    DRIVE_ZONES_PRECOMPUTED_DRIVING_DISCLAIMER;
}

function isSupportedShardStrategy(strategy: string | null): boolean {
  return strategy === null || strategy === "address_id_hash" || strategy === "address_id_hash_2";
}

async function sha256Hex(value: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle;

  if (subtle === undefined) {
    throw new Error("Web Crypto SHA-256 is unavailable.");
  }

  const digest = await subtle.digest("SHA-256", new TextEncoder().encode(value));

  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function resolveDeliveryZoneUrl(baseUrl: string, path: string): string {
  if (/^https?:\/\//iu.test(path)) {
    return path;
  }

  if (/^https?:\/\//iu.test(baseUrl)) {
    return new URL(path, baseUrl).toString();
  }

  if (path.startsWith("/")) {
    return path;
  }

  const basePath = baseUrl.replace(/[?#].*$/u, "");
  const directoryPath = basePath.endsWith("/")
    ? basePath
    : basePath.replace(/\/[^/]*$/u, "/");
  const resolved = new URL(path, `https://streetkit.invalid${directoryPath}`);

  return `${resolved.pathname}${resolved.search}${resolved.hash}`;
}

function positiveIntegerFrom(...values: unknown[]): number | null {
  for (const value of values) {
    if (typeof value === "number" && Number.isInteger(value) && value > 0) {
      return value;
    }
  }

  return null;
}

function numberFrom(...values: unknown[]): number | null {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
  }

  return null;
}

function stringFrom(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }

  return null;
}

function trimmedString(value: string | undefined): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
